<script setup lang="ts">
const { t } = useI18n()
const collectionsStore = useCollectionsStore()
const images = import.meta.glob('~/assets/homepage/*', { eager: true, import: 'default' })
</script>

<template>
  <div class="mx-auto max-w-3xl px-4 lg:max-w-[88rem] md:max-w-4xl lg:px-6 sm:px-4 sm:px-6">
    <div class="grid grid-cols-2 gap-2 py4 lg:grid-cols-4 md:grid-cols-4 sm:grid-cols-2 lg:gap-2 lg:py8">
      <div
        class="relative col-span-2 h-50 items-center overflow-hidden rd-md lg:col-span-2 sm:col-span-2 lg:h-64"
        :class="[
          collectionsStore.rootCollections.status === 'pending' ? 'n-skeleton' : 'bg-gray-100',
        ]"
      >
        <NuxtLinkLocale to="/collections/4922">
          <div v-if="collectionsStore.rootCollections.status !== 'pending'" class="h-full w-full flex items-center px5 lg:px10">
            <h2 class="z2 bg-gray-100 bg-op-80 p1 text-2xl fw300">
              {{ t('homepage.trending_categories.women_fashion') }}
            </h2>
            <NuxtImg
              :src="images['/assets/homepage/women.png'] as string"
              decoding="async"
              importance="high"
              fetchpriority="high"
              loading="eager"
              width="280"
              height="280"
              alt="Women fashion"
              class="aspect-ratio -right-0 absolute right-0 top-0 w-50 lg:w-70 lg:-right-2 lg:-top-5"
              preload
            />
          </div>
        </NuxtLinkLocale>
      </div>
      <div
        class="relative col-span-1 h-50 items-center overflow-hidden rd-md lg:col-span-1 sm:col-span-1 lg:h-64"
        :class="[
          collectionsStore.rootCollections.status === 'pending' ? 'n-skeleton' : 'bg-gray-100',
        ]"
      >
        <NuxtLinkLocale to="/collections/8513">
          <div v-if="collectionsStore.rootCollections.status !== 'pending'" class="h-full w-full flex items-center px5 md:px10">
            <h2 class="z2 rd-md bg-gray-100 bg-op-80 p1 text-2xl fw300">
              {{ t('homepage.trending_categories.toys') }}
            </h2>
            <NuxtImg
              :src="images['/assets/homepage/toy.png'] as string"
              decoding="async"
              importance="high"
              fetchpriority="high"
              loading="eager"
              width="280"
              height="280"
              alt="Toy"
              class="aspect-ratio absolute bottom-0 top-10 w-50 -right-8 lg:top-5 lg:w-70 lg:-right-13"
              preload
            />
          </div>
        </NuxtLinkLocale>
      </div>
      <div
        class="relative col-span-1 h-50 items-center overflow-hidden rd-md lg:col-span-1 sm:col-span-1 lg:h-64"
        :class="[
          collectionsStore.rootCollections.status === 'pending' ? 'n-skeleton' : 'bg-gray-100',
        ]"
      >
        <NuxtLinkLocale to="/collections/top-for-less">
          <div v-if="collectionsStore.rootCollections.status !== 'pending'" class="relative h-full w-full flex items-center justify-center from-orange-500 via-orange-600 to-orange-700 bg-gradient-to-br px5 md:px10" style="background: linear-gradient(to bottom right, #f97316, #ea580c, #c2410c);">
            <div class="absolute inset-0 animate-pulse from-orange-400/20 via-orange-500/15 via-orange-600/15 to-orange-700/10 bg-gradient-to-br" />

            <div class="absolute inset-0 animate-bounce from-white/10 via-transparent to-orange-600/10 bg-gradient-radial" style="animation-duration: 8s;" />

            <div class="absolute inset-0 bg-white/5 backdrop-blur-sm" />

            <div class="pointer-events-none absolute inset-0">
              <div class="absolute left-[15%] top-[20%] h-8 w-8 animate-bounce border border-white/10 rounded-full bg-white/8 backdrop-blur-sm duration-6000" style="animation-duration: 6s; animation-delay: 0s;" />
              <div class="absolute left-[85%] top-[30%] h-9 w-9 animate-bounce border border-white/15 rounded-full bg-white/8 backdrop-blur-sm duration-4000 delay-1500" style="animation-duration: 6s; animation-delay: 1.5s;" />
              <div class="absolute left-[70%] top-[60%] h-10 w-10 animate-bounce border border-white/15 rounded-full bg-white/8 backdrop-blur-sm duration-6000 delay-3000" style="animation-duration: 6s; animation-delay: 3s;" />
              <div class="absolute left-[25%] top-[70%] h-18 w-18 animate-bounce border border-white/15 rounded-full bg-white/8 backdrop-blur-sm duration-4000 delay-4500" style="animation-duration: 6s; animation-delay: 4.5s;" />
            </div>
            <div class="flex items-center justify-center from-white via-orange-100 via-orange-200 to-orange-300 bg-gradient-to-br bg-clip-text text-3xl text-transparent font-black leading-none tracking-tight drop-shadow-lg sm:text-4xl" style="animation-duration: 6s;">
              <div class="flex flex-col">
                <span class="uppercase">{{ t('page.top_for_less.buy_more') }}</span>
                <span class="text-xl sm:text-2xl">{{ t('page.top_for_less.for_less') }}</span>
              </div>
            </div>
          </div>
        </NuxtLinkLocale>
      </div>
      <div
        class="relative col-span-1 h-50 items-center overflow-hidden rd-md lg:col-span-1 sm:col-span-1 lg:h-64"
        :class="[
          collectionsStore.rootCollections.status === 'pending' ? 'n-skeleton' : 'bg-gray-100',
        ]"
      >
        <NuxtLinkLocale to="/collections/7091">
          <div v-if="collectionsStore.rootCollections.status !== 'pending'" class="h-full w-full flex items-center px5 md:px10">
            <h2 class="z2 rd-md bg-gray-100 bg-op-80 p1 text-2xl fw300">
              {{ t('homepage.trending_categories.home_decor') }}
            </h2>
            <NuxtImg
              :src="images['/assets/homepage/home_and_garden.png'] as string"
              decoding="async"
              loading="lazy"
              width="280"
              height="280"
              alt="Home"
              class="aspect-ratio absolute top-2 w-50 -right-9 lg:right-0 lg:w-70 lg:-right-9 lg:-top-5"
            />
          </div>
        </NuxtLinkLocale>
      </div>
      <div
        class="relative col-span-1 h-50 items-center overflow-hidden rd-md lg:col-span-1 sm:col-span-1 lg:h-64"
        :class="[
          collectionsStore.rootCollections.status === 'pending' ? 'n-skeleton' : 'bg-gray-100',
        ]"
      >
        <NuxtLinkLocale to="/collections/4725">
          <div v-if="collectionsStore.rootCollections.status !== 'pending'" class="h-full w-full flex items-center px5 md:px10">
            <h2 class="z2 rd-md bg-gray-100 bg-op-80 p1 text-2xl fw300">
              {{ t('homepage.trending_categories.mans') }}
            </h2>
            <NuxtImg
              :src="images['/assets/homepage/man.png'] as string"
              decoding="async"
              loading="lazy"
              width="280"
              height="280"
              alt="Man"
              class="aspect-ratio absolute right-0 w-70 -bottom-5 -right-10"
            />
          </div>
        </NuxtLinkLocale>
      </div>
      <div
        class="relative col-span-2 h-50 items-center overflow-hidden rd-md lg:col-span-2 sm:col-span-2 lg:h-64"
        :class="[
          collectionsStore.rootCollections.status === 'pending' ? 'n-skeleton' : 'bg-gray-100',
        ]"
      >
        <NuxtLinkLocale to="/collections/5117">
          <div v-if="collectionsStore.rootCollections.status !== 'pending'" class="h-full w-full flex items-center px5 md:px10">
            <h2 class="z2 rd-md bg-gray-100 bg-op-80 p1 text-2xl fw300">
              {{ t('homepage.trending_categories.children_fashion') }}
            </h2>
            <NuxtImg
              :src="images['/assets/homepage/baby.png'] as string"
              decoding="async"
              loading="lazy"
              alt="Baby"
              class="aspect-ratio absolute bottom-0 bottom-0 right-0 w-50 lg:right-0 lg:w-96 lg:-right-2"
              width="384"
              height="384"
            />
          </div>
        </NuxtLinkLocale>
      </div>
    </div>
  </div>
</template>
